import { useState } from "react";
import {
    Box,
    Typography,
    Button,
    Card,
    Chip,
    CircularProgress,
    Skeleton,
    TextField,
    RadioGroup,
    Radio,
    FormControlLabel,
    FormControl,
    FormLabel,
    Alert,
    Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import { getPatientAiAnalyses, submitAiReview, getClinicalImpressions } from "../../../api/services/api";
import { toast } from "react-toastify";

export function AiAnalysisReview() {
    const { id: patientProfileId, analysisId } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [accuracy, setAccuracy] = useState("CORRECT");
    const [impressionCode, setImpressionCode] = useState("");
    const [impressionLabel, setImpressionLabel] = useState("");
    const [corrections, setCorrections] = useState("");
    const [notes, setNotes] = useState("");

    const { data: analysesData, isLoading: analysesLoading } = useQuery(
        ["patientAiAnalyses", patientProfileId],
        getPatientAiAnalyses,
        { enabled: !!patientProfileId }
    );

    const { data: impressionsData } = useQuery(
        ["clinicalImpressions"],
        getClinicalImpressions
    );

    const impressions = impressionsData?.data || [];
    const analyses = analysesData?.data || [];
    const analysis = analyses.find(
        (a: any) => a.id === parseInt(analysisId || "0")
    );

    const reviewMutation = useMutation(submitAiReview, {
        onSuccess: () => {
            toast.success("Review submitted successfully");
            queryClient.invalidateQueries(["patientAiAnalyses"]);
            navigate(-1);
        },
        onError: () => {
            toast.error("Failed to submit review");
        },
    });

    const handleSubmit = () => {
        if (!impressionCode || !impressionLabel) {
            toast.warn("Please select a clinical impression");
            return;
        }
        reviewMutation.mutate({
            analysisId: parseInt(analysisId || "0"),
            body: {
                impressionCode,
                impressionLabel,
                aiAccuracy: accuracy,
                corrections: corrections || undefined,
                notes: notes || undefined,
            },
        });
    };

    if (analysesLoading) {
        return (
            <Box sx={{ p: 3 }}>
                <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2, mb: 2 }} />
                <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
            </Box>
        );
    }

    if (!analysis) {
        return (
            <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography sx={{ color: "#94a3b8" }}>Analysis not found</Typography>
                <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>
                    Go Back
                </Button>
            </Box>
        );
    }

    const hasExistingReview = !!analysis.clinician_review;

    return (
        <Box sx={{ p: 3, maxWidth: 800, mx: "auto" }}>
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(-1)}
                sx={{ mb: 2, textTransform: "none", color: "#64748b" }}
            >
                Back to Patient Exercises
            </Button>

            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                AI Analysis Review
            </Typography>

            {/* Disclaimer */}
            <Alert
                severity="warning"
                icon={<WarningAmberIcon />}
                sx={{ mb: 3, borderRadius: 2 }}
            >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    AI outputs are advisory only
                </Typography>
                <Typography variant="caption">
                    The following AI analysis is a suggestion based on the patient's voice
                    recordings. It is not a conclusive diagnosis. Please apply your
                    professional clinical judgment when reviewing.
                </Typography>
            </Alert>

            {/* AI Analysis Results */}
            <Card sx={{ p: 3, mb: 3, borderRadius: 2, border: "1px solid #c7d2fe" }}>
                <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", mb: 2 }}>
                    <SmartToyOutlinedIcon sx={{ color: "#6366f1" }} />
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        AI Findings
                    </Typography>
                    {analysis.confidence_score != null && (
                        <Chip
                            label={`Confidence: ${Math.round(analysis.confidence_score * 100)}%`}
                            size="small"
                            sx={{
                                fontWeight: 700,
                                bgcolor:
                                    analysis.confidence_score >= 0.8
                                        ? "#dcfce7"
                                        : analysis.confidence_score >= 0.6
                                            ? "#fef3c7"
                                            : "#fee2e2",
                                color:
                                    analysis.confidence_score >= 0.8
                                        ? "#166534"
                                        : analysis.confidence_score >= 0.6
                                            ? "#92400e"
                                            : "#991b1b",
                            }}
                        />
                    )}
                </Box>

                {analysis.summary && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            Summary
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#334155" }}>
                            {analysis.summary}
                        </Typography>
                    </Box>
                )}

                {analysis.aggregated_results && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                            Metrics
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                            {Object.entries(analysis.aggregated_results).map(
                                ([key, val]: any) => (
                                    <Chip
                                        key={key}
                                        label={`${key}: ${typeof val === "number" ? Math.round(val * 100) / 100 : val}`}
                                        size="small"
                                        variant="outlined"
                                        sx={{ borderColor: "#c7d2fe", color: "#4338ca" }}
                                    />
                                )
                            )}
                        </Box>
                    </Box>
                )}

                {analysis.flags && Object.keys(analysis.flags).length > 0 && (
                    <Box
                        sx={{
                            p: 2,
                            borderRadius: 2,
                            bgcolor: "#fef2f2",
                            border: "1px solid #fecaca",
                        }}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "#991b1b" }}>
                            Flags
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#991b1b" }}>
                            {JSON.stringify(analysis.flags)}
                        </Typography>
                    </Box>
                )}
            </Card>

            {/* Existing Review */}
            {hasExistingReview && (
                <Card
                    sx={{
                        p: 3,
                        mb: 3,
                        borderRadius: 2,
                        border: "1px solid #bbf7d0",
                    }}
                >
                    <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", mb: 2 }}>
                        <PersonOutlinedIcon sx={{ color: "#16a34a" }} />
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            Your Previous Review
                        </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Impression:</strong>{" "}
                        {analysis.clinician_review.impression_label}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>AI Accuracy:</strong>{" "}
                        {analysis.clinician_review.ai_accuracy}
                    </Typography>
                    {analysis.clinician_review.notes && (
                        <Typography variant="body2">
                            <strong>Notes:</strong> {analysis.clinician_review.notes}
                        </Typography>
                    )}
                </Card>
            )}

            {/* Review Form (if not yet reviewed) */}
            {!hasExistingReview && (
                <Card sx={{ p: 3, borderRadius: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                        Submit Your Clinical Review
                    </Typography>

                    {/* Clinical Impression Selection */}
                    <FormControl fullWidth sx={{ mb: 3 }}>
                        <FormLabel sx={{ fontWeight: 600, mb: 1 }}>
                            Clinical Impression
                        </FormLabel>
                        {impressions.length > 0 ? (
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                {impressions.map((imp: any) => (
                                    <Chip
                                        key={imp.code}
                                        label={imp.label_en}
                                        clickable
                                        onClick={() => {
                                            setImpressionCode(imp.code);
                                            setImpressionLabel(imp.label_en);
                                        }}
                                        sx={{
                                            fontWeight: 600,
                                            bgcolor:
                                                impressionCode === imp.code ? "#3d7169" : "#f1f5f9",
                                            color:
                                                impressionCode === imp.code ? "#fff" : "#334155",
                                            "&:hover": {
                                                bgcolor:
                                                    impressionCode === imp.code ? "#254137" : "#e2e8f0",
                                            },
                                        }}
                                    />
                                ))}
                            </Box>
                        ) : (
                            <TextField
                                placeholder="Enter impression label"
                                value={impressionLabel}
                                onChange={(e) => {
                                    setImpressionLabel(e.target.value);
                                    setImpressionCode(e.target.value.toUpperCase().replace(/\s+/g, "_"));
                                }}
                                size="small"
                                fullWidth
                            />
                        )}
                    </FormControl>

                    {/* AI Accuracy */}
                    <FormControl sx={{ mb: 3 }}>
                        <FormLabel sx={{ fontWeight: 600, mb: 1 }}>
                            AI Analysis Accuracy
                        </FormLabel>
                        <RadioGroup
                            value={accuracy}
                            onChange={(e) => setAccuracy(e.target.value)}
                            row
                        >
                            <FormControlLabel
                                value="CORRECT"
                                control={<Radio sx={{ color: "#16a34a", "&.Mui-checked": { color: "#16a34a" } }} />}
                                label="Accurate"
                            />
                            <FormControlLabel
                                value="PARTIALLY_CORRECT"
                                control={<Radio sx={{ color: "#f59e0b", "&.Mui-checked": { color: "#f59e0b" } }} />}
                                label="Partially Accurate"
                            />
                            <FormControlLabel
                                value="INCORRECT"
                                control={<Radio sx={{ color: "#dc2626", "&.Mui-checked": { color: "#dc2626" } }} />}
                                label="Inaccurate"
                            />
                        </RadioGroup>
                    </FormControl>

                    {/* Corrections (shown if not accurate) */}
                    {accuracy !== "CORRECT" && (
                        <TextField
                            label="Corrected Findings"
                            placeholder="Describe the correct findings or corrections to the AI analysis..."
                            multiline
                            rows={3}
                            value={corrections}
                            onChange={(e) => setCorrections(e.target.value)}
                            fullWidth
                            sx={{ mb: 3 }}
                        />
                    )}

                    {/* Notes */}
                    <TextField
                        label="Review Notes"
                        placeholder="Any additional clinical notes..."
                        multiline
                        rows={3}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        fullWidth
                        inputProps={{ maxLength: 2000 }}
                        sx={{ mb: 3 }}
                    />

                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={reviewMutation.isLoading || !impressionCode}
                        startIcon={
                            reviewMutation.isLoading ? (
                                <CircularProgress size={20} sx={{ color: "#fff" }} />
                            ) : (
                                <CheckCircleOutlineIcon />
                            )
                        }
                        sx={{
                            py: 1.2,
                            px: 4,
                            borderRadius: 2,
                            bgcolor: "#3d7169",
                            textTransform: "none",
                            fontWeight: 600,
                            "&:hover": { bgcolor: "#254137" },
                        }}
                    >
                        Submit Review
                    </Button>
                </Card>
            )}
        </Box>
    );
}

export default AiAnalysisReview;
